import { DateRange, LocationData } from './DashboardService';

const BASELINE_STORAGE_KEY = 'dashboard_baseline_average';

/**
 * Filter function to exclude locations that haven't appeared in at least 8 complete 8-week ranges
 * This can be used for both baseline calculations and average row calculations
 */
export const filterLocationsByEightWeekRanges = (
  locations: LocationData[],
  currentWeekEndDate: string,
  allDateRanges: DateRange[]
): LocationData[] => {
  if (!currentWeekEndDate || !allDateRanges || allDateRanges.length === 0) {
    return locations; // Return all if we can't filter
  }

  // Filter to only 8-week ranges (approximately 56 days, allow 50-65 days for flexibility)
  const eightWeekRanges = allDateRanges.filter(range => {
    const start = new Date(range.startDate + 'T00:00:00');
    const end = new Date(range.endDate + 'T00:00:00');
    const daysDiff = (end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000);
    return daysDiff >= 50 && daysDiff <= 65;
  });

  // Sort ranges chronologically (oldest first) by start date
  eightWeekRanges.sort((a, b) => a.startDate.localeCompare(b.startDate));

  // Find the current range index
  const currentRangeIndex = eightWeekRanges.findIndex(range => range.endDate === currentWeekEndDate);

  if (currentRangeIndex < 0) {
    return locations; // Can't find current range, return all
  }

  return locations.filter((location) => {
    // If no first appearance date, exclude it
    if (!location.firstAppearanceDate) {
      return false;
    }

    // Find which 8-week range the location first appeared in
    const firstAppearanceRangeIndex = eightWeekRanges.findIndex(range => 
      range.startDate === location.firstAppearanceDate
    );

    // If we can't find the first appearance range, exclude it
    if (firstAppearanceRangeIndex < 0) {
      return false;
    }

    // Calculate how many 8-week ranges have passed since first appearance
    const rangesSinceFirstAppearance = currentRangeIndex - firstAppearanceRangeIndex;

    // Only include if location has appeared in at least 8 complete 8-week ranges
    // If first appearance is at index 0, we want to include starting at index 7 (the 8th range)
    // This means: rangesSinceFirstAppearance >= 7
    return rangesSinceFirstAppearance >= 7;
  });
};

export interface BaselineData {
  weeklyToast: number;
  weeklyCatering: number;
  weeklyTotal: number;
  weeklyGfs: number;
  gfsPercent: number;
  varianceFromAvg: number;
  eightWeekTotalGfs: number;
  eightWeekTotalSales: number;
  eightWeekGfsPercent?: number | null; // Calculated 8-week GFS percent (average of individual percentages)
}

export interface BaselineChanges {
  gfsPercentPoints: number;
  weeklyTotalDelta: number | null;
  weeklyGfsDelta: number | null;
}

export interface TierBaselineData {
  baseline: BaselineData;
  baselineWeekIndex: number;
  baselineDateRange: string;
  baselineChanges: BaselineChanges | null;
}

/**
 * Calculate baseline averages from location data
 * Optionally filter out locations using a filter function (e.g., to exclude "new" locations)
 * 
 * For 8-week range dashboard: Only includes locations that have appeared in at least 8 complete
 * 8-week ranges. A location should NOT count in its first 7 ranges, and should only start
 * counting from its 8th 8-week range onwards.
 */
export const calculateBaselineAverages = (
  locations: LocationData[],
  filterFn?: (location: LocationData) => boolean,
  currentWeekEndDate?: string, // End date of the current 8-week range (YYYY-MM-DD format)
  allDateRanges?: DateRange[] // All available date ranges to determine 8-week range indices
): BaselineData => {
  // First apply the custom filter if provided
  let filteredLocations = filterFn ? locations.filter(filterFn) : locations;
  
  // Then apply the 8-week range existence filter if we have the current week end date and date ranges
  if (currentWeekEndDate && allDateRanges && allDateRanges.length > 0) {
    console.log(`[Baseline Filter] Starting filter with currentWeekEndDate: ${currentWeekEndDate}, total locations: ${filteredLocations.length}`);
    
    // Filter to only 8-week ranges (approximately 56 days, allow 50-65 days for flexibility)
    const eightWeekRanges = allDateRanges.filter(range => {
      const start = new Date(range.startDate + 'T00:00:00');
      const end = new Date(range.endDate + 'T00:00:00');
      const daysDiff = (end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000);
      return daysDiff >= 50 && daysDiff <= 65;
    });
    
    // Sort ranges chronologically (oldest first) by start date
    eightWeekRanges.sort((a, b) => a.startDate.localeCompare(b.startDate));
    
    console.log(`[Baseline Filter] Found ${eightWeekRanges.length} eight-week ranges out of ${allDateRanges.length} total ranges`);
    
    // Find the current range index
    const currentRangeIndex = eightWeekRanges.findIndex(range => range.endDate === currentWeekEndDate);
    
    const beforeFilterCount = filteredLocations.length;
    
    filteredLocations = filteredLocations.filter((location) => {
      // If no first appearance date or current range not found, exclude it
      if (!location.firstAppearanceDate || currentRangeIndex < 0) {
        return false;
      }
      
      // Find which 8-week range the location first appeared in
      const firstAppearanceRangeIndex = eightWeekRanges.findIndex(range => 
        range.startDate === location.firstAppearanceDate
      );
      
      // If we can't find the first appearance range, exclude it
      if (firstAppearanceRangeIndex < 0) {
        return false;
      }
      
      // Calculate how many 8-week ranges have passed since first appearance
      const rangesSinceFirstAppearance = currentRangeIndex - firstAppearanceRangeIndex;
      
      // Only include if location has appeared in at least 8 complete 8-week ranges
      // If first appearance is at index 0, we want to include starting at index 7 (the 8th range)
      // This means: currentRangeIndex >= firstAppearanceRangeIndex + 7
      // Or equivalently: rangesSinceFirstAppearance >= 7
      const shouldInclude = rangesSinceFirstAppearance >= 7;
      
      return shouldInclude;
    });
    
    const afterFilterCount = filteredLocations.length;
    console.log(`[Baseline Filter] Filtered ${beforeFilterCount} locations down to ${afterFilterCount} (excluded ${beforeFilterCount - afterFilterCount} locations)`);
  } else {
    console.warn(`[Baseline Filter] No currentWeekEndDate or allDateRanges provided - skipping 8-week range filter`);
  }

  if (filteredLocations.length === 0) {
    return {
      weeklyToast: 0,
      weeklyCatering: 0,
      weeklyTotal: 0,
      weeklyGfs: 0,
      gfsPercent: 0,
      varianceFromAvg: 0,
      eightWeekTotalGfs: 0,
      eightWeekTotalSales: 0,
      eightWeekGfsPercent: null,
    };
  }

  // Calculate baseline averages using the same method as the "Average" row
  const weeklyToast = filteredLocations.reduce((sum, loc) => sum + loc.weeklySalesToast, 0) / filteredLocations.length;
  const weeklyCatering = filteredLocations.reduce((sum, loc) => sum + (loc.weeklyCatering ?? 0), 0) / filteredLocations.length;
  const weeklyTotal = filteredLocations.reduce((sum, loc) => sum + loc.weeklyTotal, 0) / filteredLocations.length;
  const weeklyGfs = filteredLocations.reduce((sum, loc) => sum + (loc.weeklyGfs ?? 0), 0) / filteredLocations.length;
  
  // Calculate GFS percent as arithmetic mean of all location GFS% values
  const gfsPercent = filteredLocations.length > 0
    ? filteredLocations.reduce((sum, loc) => sum + loc.gfsPercent, 0) / filteredLocations.length
    : 0;
  
  // Variance would be 0 for baseline (it's the baseline itself)
  const varianceFromAvg = 0;

  // Calculate 8-week averages - use the same method as TierTable average row
  // Calculate individual 8-week percentages first, then average them (not average totals then divide)
  const eightWeekPercents = filteredLocations
    .map((loc) => {
      if (
        !loc.eightWeekTotalSales ||
        loc.eightWeekTotalSales <= 0 ||
        loc.eightWeekTotalGfs === null ||
        loc.eightWeekTotalGfs === undefined
      ) {
        return null;
      }
      return (loc.eightWeekTotalGfs / loc.eightWeekTotalSales) * 100;
    })
    .filter((value): value is number => value !== null && !Number.isNaN(value));

  const avgEightWeekPercent =
    eightWeekPercents.length > 0
      ? eightWeekPercents.reduce((sum, value) => sum + value, 0) / eightWeekPercents.length
      : null;
  

  // Calculate average totals (for display purposes)
  const avgEightWeekTotalGfs = filteredLocations.reduce((sum, loc) => sum + (loc.eightWeekTotalGfs ?? 0), 0) / filteredLocations.length;
  const avgEightWeekTotalSales = filteredLocations.reduce((sum, loc) => sum + loc.eightWeekTotalSales, 0) / filteredLocations.length;

  return {
    weeklyToast,
    weeklyCatering,
    weeklyTotal,
    weeklyGfs,
    gfsPercent,
    varianceFromAvg,
    // Store both the average totals and the calculated percentage
    // The percentage should be calculated from averaging individual percentages, not from dividing average totals
    eightWeekTotalGfs: avgEightWeekTotalGfs,
    eightWeekTotalSales: avgEightWeekTotalSales,
    // Store the calculated 8-week percent for use in BaselineCard
    eightWeekGfsPercent: avgEightWeekPercent,
  };
};

/**
 * Build baseline storage key
 */
const buildBaselineKey = (tierName: string, week?: DateRange): string | null => {
  if (!week) return null;
  return `${tierName}_${week.startDate}_${week.endDate}`;
};

/**
 * Build legacy baseline storage key
 */
const buildLegacyBaselineKey = (tierName: string, weekIndex: number | null | undefined): string | null => {
  if (weekIndex === undefined || weekIndex === null || weekIndex < 0) return null;
  return `${tierName}_${weekIndex}`;
};

/**
 * Store baseline data in localStorage
 */
export const storeBaseline = (
  tierName: string,
  baselineWeek: DateRange | undefined,
  baselineWeekIndex: number,
  baseline: BaselineData
): void => {
  const key = buildBaselineKey(tierName, baselineWeek);
  const legacyKey = buildLegacyBaselineKey(tierName, baselineWeekIndex);
  if (!key && !legacyKey) {
    return;
  }
  try {
    const stored = localStorage.getItem(BASELINE_STORAGE_KEY);
    const baselines = stored ? JSON.parse(stored) : {};
    if (key) {
      baselines[key] = baseline;
    }
    if (legacyKey) {
      baselines[legacyKey] = baseline;
    }
    localStorage.setItem(BASELINE_STORAGE_KEY, JSON.stringify(baselines));
  } catch (error) {
    console.error('Error storing baseline:', error);
  }
};

/**
 * Retrieve baseline data from localStorage
 */
export const getStoredBaseline = (
  tierName: string,
  baselineWeek: DateRange | undefined,
  baselineWeekIndex: number
): BaselineData | null => {
  const key = buildBaselineKey(tierName, baselineWeek);
  const legacyKey = buildLegacyBaselineKey(tierName, baselineWeekIndex);
  try {
    const stored = localStorage.getItem(BASELINE_STORAGE_KEY);
    if (!stored) return null;
    const baselines = JSON.parse(stored);
    if (key && baselines[key]) {
      return baselines[key];
    }
    if (legacyKey && baselines[legacyKey]) {
      return baselines[legacyKey];
    }
    return null;
  } catch (error) {
    console.error('Error retrieving baseline:', error);
    return null;
  }
};

